import React, { useState } from 'react';
import { 
  BarChart, Bar, LineChart, Line, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

// Import sample data
import { yearOverYearGrowth } from '../../data/timeData';
import { productCategoriesL1 } from '../../data/productData';

const YearOverYearComparison = () => {
  const [selectedCategories, setSelectedCategories] = useState(
    productCategoriesL1.slice(0, 5).map(cat => cat.name)
  );
  const [chartType, setChartType] = useState('bar');
  const [showForecast, setShowForecast] = useState(true);
  
  // Toggle category selection
  const toggleCategory = (category) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(cat => cat !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };
  
  // Prepare data for year-over-year comparison
  const prepareYoyData = () => {
    return yearOverYearGrowth
      .filter(item => selectedCategories.includes(item.category))
      .sort((a, b) => b.growth2024 - a.growth2024);
  };
  
  // Reshape data for trend comparison
  const prepareTrendData = () => {
    const filteredData = yearOverYearGrowth.filter(item => 
      selectedCategories.includes(item.category)
    );
    
    // Create separate data points for each year
    const data = [];
    
    filteredData.forEach(item => {
      data.push({
        category: item.category,
        year: '2023',
        growth: item.growth2023
      });
      
      data.push({
        category: item.category,
        year: '2024',
        growth: item.growth2024
      });
      
      if (showForecast) {
        data.push({
          category: item.category,
          year: '2025 (forecast)',
          growth: item.forecast2025
        });
      }
    });
    
    return data;
  };
  
  // Prepare data for composed chart
  const prepareComposedData = () => {
    return prepareYoyData().map(item => ({
      category: item.category,
      '2023': item.growth2023,
      '2024': item.growth2024,
      '2025': showForecast ? item.forecast2025 : null
    }));
  };
  
  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28'];
  
  // Render appropriate chart based on selection
  const renderChart = () => {
    switch(chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={500}>
            <BarChart
              data={prepareComposedData()}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="category" />
              <YAxis />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
              <Bar dataKey="2023" fill={COLORS[0]} />
              <Bar dataKey="2024" fill={COLORS[1]} />
              {showForecast && <Bar dataKey="2025" fill={COLORS[2]} />}
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={500}>
            <LineChart
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="year" 
                type="category"
                allowDuplicatedCategory={false}
              />
              <YAxis />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
              
              {selectedCategories.map((category, index) => {
                const categoryData = prepareTrendData().filter(item => 
                  item.category === category
                );
                
                return (
                  <Line 
                    key={category}
                    data={categoryData}
                    name={category}
                    dataKey="growth"
                    stroke={COLORS[index % COLORS.length]}
                    connectNulls
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        );
        
      case 'composed':
        return (
          <ResponsiveContainer width="100%" height={500}>
            <ComposedChart
              data={prepareComposedData()}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="category" />
              <YAxis />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
              <Bar dataKey="2023" fill={COLORS[0]} />
              <Bar dataKey="2024" fill={COLORS[1]} />
              {showForecast && <Line type="monotone" dataKey="2025" stroke={COLORS[2]} />}
            </ComposedChart>
          </ResponsiveContainer>
        );
        
      default:
        return null;
    }
  };
  
  // Calculate average growth
  const calculateAverages = () => {
    const filteredData = prepareYoyData();
    
    if (filteredData.length === 0) return { avg2023: 0, avg2024: 0, avgForecast: 0 };
    
    const avg2023 = filteredData.reduce((sum, item) => sum + item.growth2023, 0) / filteredData.length;
    const avg2024 = filteredData.reduce((sum, item) => sum + item.growth2024, 0) / filteredData.length;
    const avgForecast = filteredData.reduce((sum, item) => sum + item.forecast2025, 0) / filteredData.length;
    
    return { avg2023, avg2024, avgForecast };
  };
  
  const { avg2023, avg2024, avgForecast } = calculateAverages();
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Year-over-Year Comparison</h1>
        <p className="text-gray-600 mb-4">
          Compare growth rates across different years to identify trends and forecast future performance.
        </p>
        
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="kpi-card">
            <h3 className="kpi-label">2023 Growth</h3>
            <p className="kpi-value">{avg2023.toFixed(1)}%</p>
            <p className="text-sm text-gray-600">Average across selected categories</p>
          </div>
          
          <div className="kpi-card">
            <h3 className="kpi-label">2024 Growth</h3>
            <p className="kpi-value">{avg2024.toFixed(1)}%</p>
            <p className="text-sm text-gray-600">Average across selected categories</p>
          </div>
          
          <div className="kpi-card">
            <h3 className="kpi-label">2025 Forecast</h3>
            <p className="kpi-value">{avgForecast.toFixed(1)}%</p>
            <p className="text-sm text-gray-600">Average projected growth</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Chart Type</label>
            <div className="flex">
              <button
                onClick={() => setChartType('bar')}
                className={`px-3 py-2 text-sm font-medium ${
                  chartType === 'bar' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } ${chartType === 'bar' ? 'rounded-l-md' : chartType === 'line' ? '' : 'rounded-l-md'}`}
              >
                Bar Chart
              </button>
              <button
                onClick={() => setChartType('line')}
                className={`px-3 py-2 text-sm font-medium ${
                  chartType === 'line' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Line Chart
              </button>
              <button
                onClick={() => setChartType('composed')}
                className={`px-3 py-2 text-sm font-medium ${
                  chartType === 'composed' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } rounded-r-md`}
              >
                Composed
              </button>
            </div>
          </div>
          
          <div className="flex items-center">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showForecast}
                onChange={() => setShowForecast(!showForecast)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Show 2025 Forecast</span>
            </label>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-3">Product Categories</label>
          <div className="flex flex-wrap gap-2">
            {productCategoriesL1.map(category => (
              <button
                key={category.id}
                onClick={() => toggleCategory(category.name)}
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  selectedCategories.includes(category.name)
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Year-over-Year Chart */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Growth Rate Comparison</h2>
        {renderChart()}
      </div>
      
      {/* Data Table */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Growth Details by Category</h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Category</th>
                <th>2023 Growth</th>
                <th>2024 Growth</th>
                <th>YoY Change</th>
                <th>2025 Forecast</th>
                <th>Trend</th>
              </tr>
            </thead>
            <tbody>
              {prepareYoyData().map(item => {
                const yoyChange = item.growth2024 - item.growth2023;
                const trend = item.forecast2025 > item.growth2024 ? 'Increasing' : 
                              item.forecast2025 < item.growth2024 ? 'Decreasing' : 'Stable';
                
                return (
                  <tr key={item.category}>
                    <td>{item.category}</td>
                    <td>{item.growth2023.toFixed(1)}%</td>
                    <td>{item.growth2024.toFixed(1)}%</td>
                    <td className={yoyChange >= 0 ? 'positive-growth' : 'negative-growth'}>
                      {yoyChange >= 0 ? '+' : ''}{yoyChange.toFixed(1)}%
                    </td>
                    <td>{item.forecast2025.toFixed(1)}%</td>
                    <td className={
                      trend === 'Increasing' ? 'positive-growth' : 
                      trend === 'Decreasing' ? 'negative-growth' : ''
                    }>
                      {trend}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default YearOverYearComparison;